/**
 * WaterVolume — the water itself, as a backdrop.
 *
 * Distance fog alone gives every direction the same colour, which is wrong in two ways at
 * once: looking down the drop-off should get darker, and there is nothing behind the far edge
 * of the seafloor but the clear colour. Over a flat shelf that never showed, because the bed
 * filled the frame; over a 50 u cliff the eye sees a long way and the seam is a hard
 * horizontal line across the screen.
 *
 * So: a big inward-facing sphere carrying a vertical gradient, drawn before everything else
 * with no depth write. It costs one draw call and it does three jobs — it kills that seam, it
 * makes depth read as depth, and it gives the fog something to actually blend toward that
 * matches the water at that height.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { TOKENS, hexToLinear, mixRgb, type Rgb } from '../voxel/Palette.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

function v3(c: Rgb): THREE.Vector3 {
  return new THREE.Vector3(c[0], c[1], c[2]);
}

/** comfortably beyond the seafloor's own extent, so its far edge never reaches this */
const RADIUS = 520;

const VERT = /* glsl */ `
varying float vY;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vY = world.y;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uAbyss;
uniform vec3  uDeep;
uniform vec3  uShallow;
uniform vec3  uSky;
uniform float uSurface;

varying float vY;

void main() {
  float h = vY - uSurface;                    // 0 at the waterline, negative below
  // Two bands below the surface and one above. The lower boundary is placed at the depth the
  // drop-off actually reaches, so the cliff face and the water behind it darken together —
  // that agreement is the whole reason the seam disappears.
  vec3 c = mix(uAbyss, uDeep, smoothstep(-58.0, -22.0, h));
  c = mix(c, uShallow, smoothstep(-20.0, -1.0, h));
  c = mix(c, uSky, smoothstep(0.0, 12.0, h));
  gl_FragColor = vec4(c, 1.0);
}
`;

export class WaterVolume {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.SphereGeometry;
  private readonly material: THREE.ShaderMaterial;

  constructor() {
    this.geometry = new THREE.SphereGeometry(RADIUS, 24, 16);

    const fog = hexToLinear(WATER.fogColor);
    const abyss = hexToLinear(TOKENS.abyss);

    this.material = new THREE.ShaderMaterial({
      // Named so a compile failure says WHICH shader; three logs the material name and
      // nothing else identifying, and an empty name means reading every shader in the project.
      name: 'WaterVolume',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uAbyss: { value: v3(abyss) },
        uDeep: { value: v3(mixRgb(abyss, fog, 0.28)) },
        uShallow: { value: v3(fog) },
        // Above the waterline the far field is haze, not water — lighter and less saturated,
        // so a horizon that does come into frame reads as air rather than as more sea.
        uSky: { value: v3(mixRgb(fog, hexToLinear(TOKENS.bone), 0.42)) },
        uSurface: { value: OCEAN.surfaceHeight },
      },
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      lights: false,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'water-volume';
    // Drawn first and writing no depth, so it is a background in the literal sense: every
    // other object simply overwrites it, and nothing has to be sorted against it.
    this.mesh.renderOrder = -1;
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
  }

  /** rides with the player so the gradient is always centred on the viewer */
  update(x: number, z: number): void {
    this.mesh.position.set(x, 0, z);
    this.mesh.updateMatrix();
  }


  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.material.uniforms, env);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
